/**
 * Carga eventos del audit trail (wa_audit_trail) de una conversación.
 * Se usan para renderizar "pills" de timeline entre mensajes (patrón Chatwoot).
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface WaAuditEvent {
  id: string;
  conversation_id: string;
  actor_id: string | null;
  action: string;
  details: Record<string, unknown>;
  created_at: string;
}

export function useWaAuditEvents(conversationId: string | null) {
  const [events, setEvents] = useState<WaAuditEvent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!conversationId) {
      setEvents([]);
      return;
    }
    let cancelled = false;
    setLoading(true);

    (async () => {
      const { data } = await supabase
        .from('wa_audit_trail')
        .select('id, conversation_id, actor_id, action, details, created_at')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true })
        .limit(200);

      if (cancelled) return;
      setEvents((data ?? []) as WaAuditEvent[]);
      setLoading(false);
    })();

    // Realtime: append new events as they occur
    const channel = supabase
      .channel(`wa_audit_${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'wa_audit_trail',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const row = payload.new as WaAuditEvent;
          setEvents((prev) => [...prev, row]);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [conversationId]);

  return { events, loading };
}
